import { Marked } from "marked";
import DOMPurify from "dompurify";

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function isSafeHref(href: string): boolean {
  return /^(https?:|mailto:|#)/i.test(href);
}

const marked = new Marked({
  gfm: true,
  breaks: true,
  renderer: {
    html({ text }) {
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    },
    link({ href, title, tokens }) {
      const safeHref = isSafeHref(href || "") ? href : "#";
      const titleAttr = title ? ` title="${escapeAttr(String(title))}"` : "";
      const label = this.parser.parseInline(tokens);
      return `<a href="${escapeAttr(
        safeHref
      )}"${titleAttr} target="_blank" rel="noopener noreferrer">${label}</a>`;
    },
    image({ href, title, text }) {
      if (!href || !/^https?:\/\//i.test(href)) {
        return escapeAttr(text || "");
      }
      const titleAttr = title ? ` title="${escapeAttr(String(title))}"` : "";
      return `<img src="${escapeAttr(href)}" alt="${escapeAttr(
        text || ""
      )}"${titleAttr} />`;
    },
  },
});

const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    "a",
    "p",
    "br",
    "strong",
    "em",
    "del",
    "s",
    "code",
    "pre",
    "ul",
    "ol",
    "li",
    "blockquote",
    "hr",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
    "img",
  ],
  ALLOWED_ATTR: ["href", "title", "target", "rel", "src", "alt"],
  ADD_ATTR: ["target"],
  ALLOW_DATA_ATTR: false,
  ALLOWED_URI_REGEXP: /^(?:https?|mailto|#)/i,
};

let purifyHooksInstalled = false;

function installPurifyHooks(): void {
  if (purifyHooksInstalled) {
    return;
  }
  purifyHooksInstalled = true;
  DOMPurify.addHook("afterSanitizeAttributes", node => {
    if (node.nodeName === "A") {
      const href = node.getAttribute("href") || "";
      if (!isSafeHref(href)) {
        node.setAttribute("href", "#");
      }
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
    if (node.nodeName === "IMG") {
      const src = node.getAttribute("src") || "";
      if (!/^https?:\/\//i.test(src)) {
        node.removeAttribute("src");
      }
    }
  });
}

function hasDom(): boolean {
  return (
    typeof document !== "undefined" &&
    typeof document.createElement === "function"
  );
}

export function renderMarkdownHtml(content: string): string {
  const dirty = marked.parse(content) as string;
  if (!hasDom()) {
    return dirty;
  }
  installPurifyHooks();
  return DOMPurify.sanitize(dirty, PURIFY_CONFIG);
}
